import { useCallback, useEffect, useMemo, useState } from 'react';
import { barrattAPI, DivisionScoreReportRow } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Download, Loader2, AlertCircle, Search, ChevronLeft, ChevronRight, ArrowDownWideNarrow, ArrowUpNarrowWide, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import * as XLSX from 'xlsx';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const PAGE_SIZES = [100, 250, 500] as const;
type SortDir = 'asc' | 'desc';
/** Column to sort on: the supplier name, the average, or one division's score. */
type SortKey = 'supplier' | 'average' | { division: string };

/** Colour band matching the scoring legend (1=20 Poor … 5=100 Excellent). */
function scoreBadgeClass(displayScore: number): string {
  if (displayScore >= 100) return 'bg-green-100 text-green-800';
  if (displayScore >= 80) return 'bg-emerald-100 text-emerald-800';
  if (displayScore >= 60) return 'bg-yellow-100 text-yellow-800';
  if (displayScore >= 40) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
}

export default function DivisionScoreReport() {
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [rows, setRows] = useState<DivisionScoreReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('average');
  const [sortDir, setSortDir] = useState<SortDir>('asc');   // average ascending = worst first
  const [pageSize, setPageSize] = useState<number>(100);
  const [page, setPage] = useState(1);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await barrattAPI.getDivisionScoreReport(selectedQuarter, selectedYear);
      setRows(res.data.data || []);
    } catch (err: unknown) {
      console.error('Failed to load score report:', err);
      setError('Failed to load the score report. Try refreshing the page.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedQuarter, selectedYear]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Column set = every division that scored anything this quarter, sorted.
  const divisions = useMemo(() => {
    const names = new Set<string>();
    rows.forEach((r) => Object.keys(r.scores).forEach((d) => names.add(d)));
    return Array.from(names).sort();
  }, [rows]);

  // Search, then order (worst first by default), then page. Rank is the
  // position in the ordered list so it survives paging.
  const orderedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term ? rows.filter((r) => r.supplier.toLowerCase().includes(term)) : rows;
    const value = (r: DivisionScoreReportRow): number | string | null => {
      if (sortKey === 'supplier') return r.supplier.toLowerCase();
      if (sortKey === 'average') return r.average_display_score;
      return r.scores[sortKey.division] ? r.scores[sortKey.division].display_score : null;
    };
    const sign = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = value(a); const vb = value(b);
      if (va === null && vb === null) return a.supplier.localeCompare(b.supplier);
      if (va === null) return 1;            // unscored always last
      if (vb === null) return -1;
      if (va < vb) return -1 * sign;
      if (va > vb) return 1 * sign;
      return a.supplier.localeCompare(b.supplier);
    });
  }, [rows, search, sortKey, sortDir]);

  const isSortedBy = (key: SortKey) =>
    typeof key === 'string' ? sortKey === key : typeof sortKey !== 'string' && sortKey.division === key.division;
  const sortBy = (key: SortKey) => {
    if (isSortedBy(key)) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortIcon = (key: SortKey) =>
    !isSortedBy(key) ? <ArrowUpDown className="h-3 w-3 ml-1 inline text-muted-foreground" />
      : sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 inline" /> : <ArrowDown className="h-3 w-3 ml-1 inline" />;
  const pageCount = Math.max(1, Math.ceil(orderedRows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = orderedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setPage(1); }, [search, sortKey, sortDir, pageSize, selectedQuarter, selectedYear]);

  const pager = (position: 'top' | 'bottom') => (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid={`score-pager-${position}`}>
      <span className="text-sm text-muted-foreground">
        {orderedRows.length === 0
          ? 'No suppliers match'
          : `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, orderedRows.length)} of ${orderedRows.length}`}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Show</span>
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
          <SelectTrigger className="w-20" aria-label="Rows per page"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setPage(safePage - 1)} disabled={safePage <= 1} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm tabular-nums">Page {safePage} of {pageCount}</span>
        <Button variant="outline" size="sm" onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount} aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  /** Excel of what is on screen after search and sort — every page, not just the current one. */
  const exportExcel = () => {
    const header = ['Rank', 'Supplier', ...divisions, 'Average'];
    const body = orderedRows.map((r, i) => [
      i + 1,
      r.supplier,
      ...divisions.map((d) => (r.scores[d] ? r.scores[d].display_score : '')),
      r.average_display_score,
    ]);
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    sheet['!cols'] = [{ wch: 6 }, { wch: 44 }, ...divisions.map(() => ({ wch: 16 })), { wch: 10 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, `Scores ${selectedQuarter} ${selectedYear}`);
    const suffix = search.trim() ? `-${search.trim().replace(/[^a-z0-9]+/gi, '_').toLowerCase()}` : '';
    XLSX.writeFile(book, `division-score-report-${selectedQuarter}-${selectedYear}${suffix}.xlsx`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Division Score Report</h1>
          <p className="text-muted-foreground">
            Supplier scores across every division, ranked by quarterly average
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportExcel} disabled={rows.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          Export Excel
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Quarter:</label>
              <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {quarters.map((q) => (
                    <SelectItem key={q} value={q}>
                      {q}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Year:</label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report */}
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-lg font-semibold mb-1">
            Score Report - {selectedQuarter} {selectedYear}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {rows.length > 0
              ? `${rows.length} suppliers scored by ${divisions.length} divisions`
              : ' '}
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Loading score report…
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-destructive">
              <AlertCircle className="h-5 w-5 mr-2" />
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No scores recorded for {selectedQuarter} {selectedYear}.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-72 max-w-full">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search supplier…"
                         className="pl-8" aria-label="Search supplier" data-testid="score-search" />
                </div>
                <Button variant="outline" size="sm" data-testid="score-sort"
                        onClick={() => { setSortKey('average'); setSortDir(sortKey === 'average' && sortDir === 'asc' ? 'desc' : 'asc'); }}>
                  {sortKey === 'average' && sortDir === 'desc'
                    ? <><ArrowUpNarrowWide className="h-4 w-4 mr-2" />Best first</>
                    : <><ArrowDownWideNarrow className="h-4 w-4 mr-2" />Worst first</>}
                </Button>
              </div>
              {pager('top')}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">Rank</TableHead>
                    <TableHead className="min-w-48 sticky left-0 bg-background cursor-pointer select-none" onClick={() => sortBy('supplier')} data-testid="sort-supplier">
                      Supplier{sortIcon('supplier')}
                    </TableHead>
                    {divisions.map((d) => (
                      <TableHead key={d} className="text-center whitespace-nowrap cursor-pointer select-none" onClick={() => sortBy({ division: d })}>
                        {d}{sortIcon({ division: d })}
                      </TableHead>
                    ))}
                    <TableHead className="text-center cursor-pointer select-none" onClick={() => sortBy('average')} data-testid="sort-average">
                      Average{sortIcon('average')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((row, i) => (
                    <TableRow key={row.supplier}>
                      <TableCell className="font-medium">{(safePage - 1) * pageSize + i + 1}</TableCell>
                      <TableCell className="font-medium sticky left-0 bg-background whitespace-nowrap">
                        {row.supplier}
                      </TableCell>
                      {divisions.map((d) => {
                        const cell = row.scores[d];
                        return (
                          <TableCell key={d} className="text-center">
                            {cell ? (
                              <Badge
                                variant="secondary"
                                className={scoreBadgeClass(cell.display_score)}
                                title={cell.comments || undefined}
                              >
                                {cell.display_score}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">–</span>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-semibold">
                        {row.average_display_score}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
              {pager('bottom')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
